import { jsx, jsxs } from 'react/jsx-runtime';
import React, { useRef, useState, useEffect, useCallback, createElement, useMemo } from 'react';
import { useAutoFocus, onActivate, RichText, usePrevious, useAutoComplete, scrollContainerToChild, MediaDisplay, Dropdown } from 'api-sdk-react';
import { richToPlain, isInputNode, isBooleanInputField, isSingleChoiceInputField, isEmptyNodeInput, BooleanDisplayType, BooleanInputUtils, createEmptyNodeInput, createDynamicChoiceApiRoutesFromUrl, DynamicChoiceInputUtils, FileInputUtils, NumberInputUtils, SingleChoiceDisplayType, SingleChoiceInputUtils, TextInputUtils, ReaderModule } from 'api-sdk';
import 'react-day-picker';
import 'react-day-picker/dist/style.css';
import * as Sentry from '@sentry/react';

function ReaderIcon(props) {
    const className = "cdc-reader-icon material-icons " + (props.className || "");
    const iconRef = useRef(undefined);
    useAutoFocus(iconRef.current, props.autoFocus);
    const isClickable = props.tabIndex != null;
    return (jsx("div", { className: className, style: props.style, onClick: props.onClick, onKeyDown: props.onKeyDown, "aria-hidden": !isClickable, "aria-label": props.label, role: isClickable ? "button" : "none", "aria-pressed": props.pressed, ref: iconRef, tabIndex: props.tabIndex, children: jsx("i", { className: "cdc-reader-icon-inner material-icons material-symbols-outlined", "aria-hidden": !isClickable || !!props.label, children: props.iconName }) }));
}

function useAccessibilityOptions(cfg) {
    const [accessibilitySettings, setAccessibilitySettings] = useState({ isUsingScreenReader: false });
    const [isExpanded, setIsExpanded] = useState(false);
    useEffect(() => {
        const keyboardListener = (e) => {
            if (e.code == "KeyK" && e.altKey) {
                setAccessibilitySettings(Object.assign(Object.assign({}, accessibilitySettings), { isUsingScreenReader: !accessibilitySettings.isUsingScreenReader }));
            }
        };
        window.addEventListener("keydown", keyboardListener);
        return () => window.removeEventListener("keydown", keyboardListener);
    });
    // HEY HEY HEY HEY HEY Reduce motion & aria live for reports!!!!!!!!!!!!!
    const renderCheckbox = (label, field) => (jsxs("div", Object.assign({ className: "option", "aria-label": label, role: "checkbox" }, onActivate(() => setAccessibilitySettings(Object.assign(Object.assign({}, accessibilitySettings), { [field]: !accessibilitySettings[field] }))), { "aria-checked": accessibilitySettings[field], children: [jsx(ReaderIcon, { iconName: accessibilitySettings[field] ? "check_box" : "check_box_outline_blank" }), jsx("div", { className: "label", "aria-hidden": true, children: label })] })));
    const element = (jsxs("div", { className: "accessibility-options " + (isExpanded ? "shown " : ""), children: [jsxs("div", { className: "options", children: [jsx("div", { className: "heading", children: cfg.messages.accessibility }), renderCheckbox(cfg.messages.accessibilityScreenReader, "isUsingScreenReader")] }), jsx(ReaderIcon, Object.assign({ className: "options-toggle", iconName: "accessibility_new", "aria-pressed": isExpanded }, onActivate(() => setIsExpanded(!isExpanded)), { label: cfg.messages.accessibility }))] }));
    return [accessibilitySettings, element];
}

function ProgressIndicator(props) {
    return (jsxs("div", Object.assign({}, props, { className: (props.className || "") + " progress-indicator", children: [jsx("div", { className: "inner" }), jsx("div", { className: "inner" }), jsx("div", { className: "inner" })] })));
}

function ActionButton(props) {
    let content;
    let className = "action-button " + (props.className || "") + (props.disabled ? " disabled" : "");
    const disabled = !!props.disabled || !!props.loading;
    if (!props.loading) {
        const text = props.text ? (jsx("span", { children: props.text }, "text")) : null;
        const icon = props.iconName ? (jsx(ReaderIcon, { className: "icon", iconName: props.iconName }, "icon")) : null;
        content = [text, icon];
        if (props.iconPosition === "start") {
            content.reverse();
        }
    }
    else {
        content = (jsx(ProgressIndicator, {}));
        className += " loading disabled";
    }
    return (jsx("div", Object.assign({ style: props.style }, onActivate(!disabled && typeof props.onClick == "function" ? props.onClick : undefined), { "aria-disabled": disabled, role: "button", className: className, children: content })));
}

function ErrorState(props) {
    return (jsxs("div", { style: props.style, className: "error-state " + (props.className || ""), children: [jsx("img", { draggable: "false", src: "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48cGF0aCBkPSJNMTIgMGMtNi42MjcgMC0xMiA1LjM3My0xMiAxMnM1LjM3MyAxMiAxMiAxMiAxMi01LjM3MyAxMi0xMi01LjM3My0xMi0xMi0xMnptOSAxMmMwIDEuOTQtLjYyNCAzLjczNS0xLjY3MiA1LjIwN2wtMTIuNTM1LTEyLjUzNWMxLjQ3Mi0xLjA0OCAzLjI2Ny0xLjY3MiA1LjIwNy0xLjY3MiA0Ljk2MiAwIDkgNC4wMzggOSA5em0tMTggMGMwLTEuOTQuNjI0LTMuNzM1IDEuNjcyLTUuMjA3bDEyLjUzNCAxMi41MzRjLTEuNDcxIDEuMDQ5LTMuMjY2IDEuNjczLTUuMjA2IDEuNjczLTQuOTYyIDAtOS00LjAzOC05LTl6Ii8+PC9zdmc+" }), jsx("div", { className: "description", children: props.error || props.config.messages.runError }), jsx("div", { className: "reference-info", children: "@ " + window.location.pathname }), jsx("div", { onClick: () => window.location.reload(), className: "retry-button", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", height: "24px", viewBox: "0 -960 960 960", width: "24px", fill: "#5f6368", children: jsx("path", { d: "M480-160q-134 0-227-93t-93-227q0-134 93-227t227-93q69 0 132 28.5T720-690v-110h80v280H520v-80h168q-32-56-87.5-88T480-720q-100 0-170 70t-70 170q0 100 70 170t170 70q77 0 139-44t87-116h84q-28 106-114 173t-196 67Z" }) }) })] }));
}

function HTMLDisplay(props) {
    const [html, setHtml] = useState("");
    const [downloadError, setDownloadError] = useState(false);
    const [targetUrl, setTargetUrl] = useState(undefined);
    const isLoading = !downloadError && !html;
    useEffect(() => {
        if (props.url && (props.url != targetUrl)) {
            // Start download
            setTargetUrl(props.url);
            setDownloadError(false);
            setHtml("");
            const onError = (e) => {
                console.warn("Failed to download HTML:", e);
                setDownloadError(true);
            };
            fetch(props.url)
                .then(resp => {
                if (resp.ok) {
                    resp.text().then(setHtml).catch(onError);
                }
                else {
                    setDownloadError(true);
                }
            })
                .catch(onError);
        }
    }, [props.url, targetUrl, html, downloadError]);
    let content = null;
    if (downloadError) {
        content = (jsx(ErrorState, { config: props.config }));
    }
    else if (isLoading) {
        content = (jsxs("div", { className: "skeleton", children: [jsx("div", { className: "header" }), jsx("div", { className: "paragraph" }), jsx("div", { className: "paragraph" })] }));
    }
    else {
        content = (jsx("div", { dangerouslySetInnerHTML: { __html: html } }));
    }
    const className = "html-display "
        + (isLoading ? "loading " : "")
        + (downloadError ? "error " : "")
        + (props.className || "");
    return (jsx("div", { style: props.style, className: className, children: content }));
}

var ActivationMode;
(function (ActivationMode) {
    ActivationMode["MOUSE"] = "MOUSE";
    ActivationMode["KEYBOARD"] = "KEYBOARD";
})(ActivationMode || (ActivationMode = {}));

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg"];

var IconName;
(function (IconName) {
    IconName["PLAY"] = "play";
    IconName["DOWNLOAD"] = "download";
    IconName["FILE"] = "file";
    IconName["CHEVRON_LEFT"] = "chevron-left";
    IconName["CHEVRON_RIGHT"] = "chevron-right";
    IconName["EDIT"] = "edit";
    IconName["DROPDOWN_ARROW"] = "dropdown-arrow";
    IconName["CLEAR"] = "clear";
})(IconName || (IconName = {}));

function HighlightedMedia(props) {
    const [urls, setUrls] = React.useState(null);
    // Obtain URLs for all files
    useEffect(() => {
        Promise.all(props.media.map(m => props.fileUrlProvider(m.file.id)))
            .then(urls => {
            const urlMap = {};
            props.media.forEach((m, idx) => {
                urlMap[m.file.id] = urls[idx];
            });
            setUrls(urlMap);
        })
            .catch(console.error);
    }, [props.media.map(m => m.file.id).join(";")]);
    return urls ? (jsx(HighlightedMediaWithUrls, Object.assign({}, props, { urls: urls }))) : null;
}
function HighlightedMediaWithUrls(props) {
    const mediaViews = props.media.map((fd, idx) => {
        const ref = fd.file;
        const url = props.urls[ref.id];
        const animationDelay = (400 + 80 * idx) + "ms";
        const captionKey = "caption-" + url;
        let caption = null;
        if (fd.caption) {
            caption = props.renderCaption(fd.caption, animationDelay);
        }
        else if (idx) {
            caption = (jsx("div", { className: "media-separator" }, captionKey));
        }
        let content;
        if (ref.extension === "html") {
            content = (jsx(HTMLDisplay, { config: props.config, url: url }));
        }
        else if (IMAGE_EXTENSIONS.includes(ref.extension)) {
            content = (jsx("img", { alt: fd.caption, src: url }));
        }
        else {
            content = (jsxs("a", { tabIndex: 0, href: url, target: "_blank", className: "download-button", rel: "noreferrer", children: [jsx(ReaderIcon, { className: "icon", iconName: "cloud_download" }), ("Download")] }, url));
        }
        return [
            caption,
            jsx("div", { style: { animationDelay: animationDelay }, className: "media-container", children: content }, url)
        ];
    });
    return mediaViews.length ? (jsx("div", { className: "highlighted-media " + (props.animateEntry ? "animate-entry" : ""), children: mediaViews })) : null;
}

function InfoBox(props) {
    const closeButton = props.onClose ? (jsx(ReaderIcon, Object.assign({ iconName: "clear", autoFocus: true }, onActivate(props.onClose), { label: props.config.messages.close }))) : null;
    return (jsxs("div", { className: "info-box", children: [jsxs("div", { className: "header", children: [jsx(ReaderIcon, { iconName: "help" }), jsx("div", { className: "text", children: props.heading }), closeButton] }), jsx("div", { "aria-live": "polite", children: jsx(RichText, { text: props.text }) })] }));
}

function ProgressBar(props) {
    const percentText = Math.round(props.progress * 100);
    return (jsxs("div", { className: "progress-bar " + (props.className || ""), style: props.style, "aria-label": percentText + "%", children: [jsx("div", { className: "progress-bar-background", "aria-hidden": true }), jsx("div", { className: "progress-bar-progress", style: { transform: "translateX(" + (-Math.round(100 - props.progress * 100)) + "%)" }, "aria-hidden": true })] }));
}

function Scrollbar(props) {
    const [isDragging, setIsDragging] = useState(false);
    const [lastHovered, setLastHovered] = useState(0);
    const sliderRef = useRef(undefined);
    const target = document.getElementById(props.for);
    const isVertical = props.orientation == "vertical";
    const sizeProperty = isVertical ? "clientHeight" : "clientWidth";
    const scrollProperty = isVertical ? "scrollTop" : "scrollLeft";
    // Compute and update slider size and placement
    const updateSliderPosition = useCallback(() => {
        var _a, _b;
        if (target && sliderRef.current) {
            const targetSize = target[sizeProperty];
            const scrollSize = target.children[0][sizeProperty] - targetSize;
            if (scrollSize >= 0) {
                (_a = sliderRef.current.parentElement) === null || _a === void 0 ? void 0 : _a.classList.remove("hidden");
            }
            else {
                (_b = sliderRef.current.parentElement) === null || _b === void 0 ? void 0 : _b.classList.add("hidden");
            }
            const sliderSize = sliderRef.current[sizeProperty];
            const translateFunction = isVertical ? "translateY" : "translateX";
            sliderRef.current.style.transform = translateFunction + "(" + ((targetSize - sliderSize - 4) * (target[scrollProperty] / scrollSize)) + "px)";
        }
    }, [target, sliderRef]);
    const onHoverSlider = useCallback(() => {
        updateSliderPosition();
        if (sliderRef.current && !sliderRef.current.classList.contains("visible")) {
            sliderRef.current.classList.add("visible");
            setLastHovered(new Date().getTime());
            setTimeout(() => {
                if (new Date().getTime() - lastHovered > 2300) {
                    sliderRef.current.classList.remove("visible");
                }
            }, 2500);
        }
    }, [updateSliderPosition, sliderRef, lastHovered]);
    const scrollListener = useCallback((e) => {
        if (e && e.target && !isDragging) {
            onHoverSlider();
            updateSliderPosition();
        }
    }, [onHoverSlider, updateSliderPosition, isDragging]);
    // This is needed to remove the old listener
    const previousScrollListener = usePrevious(scrollListener);
    // Add scroll listener to target
    useEffect(() => {
        if (target) {
            target.removeEventListener("scroll", previousScrollListener);
            target.addEventListener("scroll", scrollListener);
            // Add classname to hide browser scrollbar
            if (!target.className.includes("use-cdc-scrollbar")) {
                target.className += " use-cdc-scrollbar";
            }
        }
    }, [target, scrollListener, previousScrollListener]);
    // Add mouse handlers
    useEffect(() => {
        const mouseUpListener = () => {
            setIsDragging(false);
            if (sliderRef.current && sliderRef.current.parentElement) {
                sliderRef.current.parentElement.classList.remove("scrolling");
            }
        };
        const mouseMoveListener = (e) => {
            if (isDragging && sliderRef.current && sliderRef.current.parentElement && target) {
                e.preventDefault();
                updateScrollPositionFromMousePosition(target, sliderRef.current, isVertical ? e.clientY : e.clientX, sizeProperty, scrollProperty, isVertical ? "y" : "x");
                updateSliderPosition();
            }
        };
        window.addEventListener("mouseup", mouseUpListener);
        window.addEventListener("mousemove", mouseMoveListener);
        return () => {
            window.removeEventListener("mouseup", mouseUpListener);
            window.removeEventListener("mousemove", mouseMoveListener);
        };
    }, [props.for, isDragging, sliderRef, target, updateSliderPosition]);
    return (jsx("div", { className: "cdc-scrollbar " + (isVertical ? "vertical" : "horizontal"), onMouseOver: onHoverSlider, onClick: e => {
            e.preventDefault();
            updateScrollPositionFromMousePosition(target, sliderRef.current, isVertical ? e.clientY : e.clientX, sizeProperty, scrollProperty, isVertical ? "y" : "x");
            updateSliderPosition();
        }, "aria-hidden": true, children: jsx("div", { className: "slider", ref: sliderRef, onMouseDown: e => {
                var _a;
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
                if (sliderRef.current) {
                    (_a = sliderRef.current.parentElement) === null || _a === void 0 ? void 0 : _a.classList.add("scrolling");
                }
            } }) }));
}
function updateScrollPositionFromMousePosition(target, slider, mousePos, sizeProperty, scrollProperty, direction) {
    const scrollBarSize = slider.parentElement[sizeProperty];
    let scroll = mousePos - slider.parentElement.getBoundingClientRect()[direction];
    scroll = Math.max(0, Math.min(scrollBarSize, scroll)) / scrollBarSize;
    const targetSize = target[sizeProperty];
    const scrollSize = target.children[0][sizeProperty] - targetSize;
    target[scrollProperty] = scrollSize * scroll;
}

/**
 * Get a CSS variable value from an element
 * Return:
 *  - textValue: The value of the CSS variable as a string
 *  - numberValue: The value of the CSS variable as a number, if it is a number. This also applies when the value has a unit (e.g. "10px", "2em", ...)
 *  - durationValue: The value of a duration in ms if the value is a duration
 *  - isNumeric: Whether the original value is a number (i.e. without a unit)
 */
function getCSSVariable(element, variableName) {
    function getAsText() {
        if (element) {
            const computedStyle = getComputedStyle(element);
            return computedStyle
                ? computedStyle.getPropertyValue(variableName)
                : null;
        }
    }
    function getAsNumber() {
        const value = getAsText();
        if (value != null) {
            const parsed = parseFloat(value.replace(/[^0-9-.]/g, ""));
            if (!isNaN(parsed)) {
                return parsed;
            }
        }
        return null;
    }
    function getAsDuration() {
        const value = getAsText();
        if (value != null) {
            // If "0.x" is abbreviated, expand it (this is not strictly needed, since parseFloat recognizes this format too, but just to be safe)
            const expandedValue = value.startsWith(".") ? `0${value}` : value;
            const parsed = parseFloat(expandedValue.replace(/[^0-9-.]/g, ""));
            if (!isNaN(parsed)) {
                return expandedValue.includes("ms") ? parsed : parsed * 1000;
            }
        }
        return null;
    }
    const textValue = getAsText();
    const isNumeric = textValue != null && !isNaN(parseFloat(textValue));
    return {
        textValue,
        isNumeric,
        numberValue: isNumeric ? parseFloat(textValue) : getAsNumber(),
        durationValue: getAsDuration(),
    };
}

function AutoComplete(props) {
    var _a, _b, _c, _d, _e;
    const [autoComplete, updateAutoComplete] = useAutoComplete(props.autoCompleteConfig);
    const optionContainer = useRef(undefined);
    // Update state when props change
    const previousProps = usePrevious(props);
    const previousSelectedOptionKey = usePrevious(autoComplete.selectedOptionKey);
    const isExpanded = ((_a = autoComplete.result) === null || _a === void 0 ? void 0 : _a.options.length) && !autoComplete.selectedOptionKey && ((_b = autoComplete.query) === null || _b === void 0 ? void 0 : _b.length) > 0;
    useEffect(() => {
        if ((props.value != (previousProps === null || previousProps === void 0 ? void 0 : previousProps.value)) && props.value != autoComplete.selectedOptionKey) {
            updateAutoComplete.setSelectedOption(props.value);
        }
    }, [props.value, autoComplete.selectedOptionKey, previousProps === null || previousProps === void 0 ? void 0 : previousProps.value, updateAutoComplete]);
    /* eslint-disable react-hooks/exhaustive-deps */
    // Call onChange when selection changes. This is done in an effect, because useAutoComplete can also change the selection
    useEffect(() => {
        if ((autoComplete.selectedOptionKey != previousSelectedOptionKey) && props.value != autoComplete.selectedOptionKey) {
            props.onChange(autoComplete.selectedOptionKey, autoComplete.result.options.find(opt => opt.key == autoComplete.selectedOptionKey));
            updateAutoComplete.setQuery("");
        }
    }, [autoComplete.selectedOptionKey, props.value, previousSelectedOptionKey, props.onChange, updateAutoComplete]);
    /* eslint-enable react-hooks/exhaustive-deps */
    // Scroll to option when focus changes
    useEffect(() => {
        var _a;
        if (((_a = autoComplete.result) === null || _a === void 0 ? void 0 : _a.options) != undefined && optionContainer.current != undefined) {
            scrollContainerToChild(optionContainer.current, autoComplete.focusedIdx);
        }
    }, [autoComplete.focusedIdx, (_c = autoComplete.result) === null || _c === void 0 ? void 0 : _c.options, optionContainer]);
    const selectedOptionKey = autoComplete.selectedOptionKey;
    function renderOption(option, showCheckbox, optionIdx, onClick, onClear) {
        const checkboxIcon = showCheckbox ? (jsx(ReaderIcon, { className: 'selected-icon', iconName: "check_circle" })) : null;
        const mediaContent = option.media ? (jsx("div", { className: 'media-content', children: jsx(MediaDisplay, { media: option.media, fileUrlProvider: props.fileUrlProvider }) })) : null;
        let actionIcon = null;
        if (onClear) {
            actionIcon = (jsx(ReaderIcon, Object.assign({ className: "action-icon", iconName: "clear" }, onActivate(onClear))));
        }
        else if (option.annotation && props.onClickAnnotation) {
            actionIcon = (jsx(ReaderIcon, Object.assign({ className: "action-icon", iconName: "help", autoFocus: false }, onActivate(e => {
                e.preventDefault();
                e.stopPropagation();
                props.onClickAnnotation(e, option.annotation, option);
            }))));
        }
        const optionInteractionProps = isExpanded ? onActivate(onClick) : { tabIndex: -1 };
        return (createElement("div", Object.assign({}, optionInteractionProps, { onMouseMove: () => {
                if (onClick != undefined) {
                    updateAutoComplete.setFocusedIdx(optionIdx);
                }
            }, className: "option " + ((onClick && optionIdx == autoComplete.focusedIdx) ? "selected " : "") + (onClick ? "clickable " : ""), key: String(option.key) }),
            checkboxIcon,
            mediaContent,
            jsx("div", { className: "label", title: richToPlain(option.label), children: jsx(RichText, { text: option.label }) }),
            actionIcon));
    }
    let mainOptionView;
    if (selectedOptionKey != undefined) {
        if (autoComplete.selectedOptionFetchFailed) {
            // TODO this isn't so beautiful...
            mainOptionView = renderOption({ key: undefined, label: "N/A" }, true);
        }
        else if (autoComplete.selectedOption) {
            mainOptionView = renderOption(autoComplete.selectedOption, true, undefined, undefined, props.onClear);
        }
        else { // Option info is loading
            // TODO ...neither is this
            mainOptionView = renderOption({ key: undefined, label: "..." }, true);
        }
    }
    else {
        mainOptionView = props.renderInput ? props.renderInput(autoComplete.query, updateAutoComplete.setQuery) : (jsx("input", { placeholder: props.config.messages.inputText, value: autoComplete.query, onChange: e => updateAutoComplete.setQuery(e.target.value), autoFocus: props.autoFocus, tabIndex: 0 }));
    }
    let optionListContent;
    let optionListHeight;
    let optionHeight = 60;
    let inputHeight = optionHeight;
    // Get option height from CSS
    if (optionContainer.current) {
        optionHeight = getCSSVariable(optionContainer.current, "--option-height").numberValue;
        inputHeight = getCSSVariable(optionContainer.current, "--input-height").numberValue;
    }
    if ((_e = (_d = autoComplete.result) === null || _d === void 0 ? void 0 : _d.options) === null || _e === void 0 ? void 0 : _e.length) {
        optionListContent = autoComplete.result.options.map((opt, idx) => renderOption(opt, false, idx, () => updateAutoComplete.setSelectedOption(opt.key)));
        optionListHeight = autoComplete.result.options.length * optionHeight;
    }
    else if (autoComplete.isLoading) {
        optionListContent = (jsxs("div", { className: "loading-results", children: [jsx("div", { className: "option-skeleton" }), jsx("div", { className: "option-skeleton" }), jsx("div", { className: "option-skeleton" }), jsx("div", { className: "option-skeleton" }), jsx("div", { className: "option-skeleton" })] }));
        optionListHeight = 5 * optionHeight;
    }
    else {
        optionListContent = (jsx("div", { className: 'no-results', children: props.config.messages.noResults }));
        optionListHeight = optionHeight * 2;
    }
    const className = "cdc-autocomplete "
        + (isExpanded ? "expanded " : "")
        + (selectedOptionKey != undefined ? "has-value " : "")
        + (props.className || "");
    const containerHeight = isExpanded ? optionListHeight + inputHeight : inputHeight;
    return (jsxs("div", { className: className, style: { height: `${containerHeight}px` }, children: [jsx("div", { className: "selected-option", children: mainOptionView }), jsx("div", { className: "option-list", style: { height: !isExpanded ? "0" : (optionListHeight + "px") }, ref: optionContainer, children: optionListContent })] }));
}

var SelectComponentType;
(function (SelectComponentType) {
    SelectComponentType["SINGLE_CHOICE"] = "SINGLE_CHOICE";
    SelectComponentType["MULTIPLE_CHOICE"] = "MULTIPLE_CHOICE";
})(SelectComponentType || (SelectComponentType = {}));
function SelectComponent(props) {
    const isMultipleChoice = props.selectType === SelectComponentType.MULTIPLE_CHOICE;
    const mediaOptions = [];
    const textOptions = [];
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const keyboardListener = (e) => {
            if (!isMultipleChoice && props.options.length) {
                const sortedOptions = props.options.sort((a, b) => a.idx - b.idx);
                const changeSelectionIdx = (changeBy) => {
                    const currentIdx = sortedOptions.findIndex(opt => opt.id == props.value);
                    const nextIdx = Math.max(0, Math.min(props.options.length - 1, currentIdx + changeBy));
                    props.onChange(sortedOptions[nextIdx].id, false);
                    e.stopPropagation();
                    e.preventDefault();
                };
                if (e.key == "ArrowDown") {
                    changeSelectionIdx(1);
                }
                else if (e.key == "ArrowUp") {
                    changeSelectionIdx(-1);
                }
                else if (e.key === "Enter" && props.value != null) {
                    props.onChange(props.value, true);
                }
            }
        };
        window.addEventListener("keydown", keyboardListener);
        return () => window.removeEventListener("keydown", keyboardListener);
    }, [props.onChange, isMultipleChoice, props.options]);
    /* eslint-enable react-hooks/exhaustive-deps */
    function handleClick(opt) {
        var _a;
        function onChange(val) {
            props.onChange(val, true);
        }
        if (props.selectType === SelectComponentType.MULTIPLE_CHOICE) {
            const val = ((_a = props.value) !== null && _a !== void 0 ? _a : []);
            if (val.includes(opt.id)) {
                const nextSelection = val.filter(id => id !== opt.id);
                onChange(nextSelection.length === 0 ? undefined : nextSelection);
            }
            else {
                onChange(val.concat(opt.id));
            }
        }
        else if (props.value == opt.id && props.isOptional) {
            onChange(undefined);
        }
        else {
            onChange(opt.id);
        }
    }
    props.options.sort((a, b) => a.idx - b.idx).forEach((opt, idx) => {
        var _a;
        const target = opt.media ? mediaOptions : textOptions;
        target.push(jsx(SelectOption, { option: opt, autoFocus: !idx && props.autoFocus, showCheckbox: props.showCheckboxes, selected: (isMultipleChoice && (props.value != null)
                ? ((_a = props.value) !== null && _a !== void 0 ? _a : []).includes(opt.id)
                : props.value === opt.id), handleClick: handleClick, fileUrlProvider: props.fileUrlProvider, onClickAnnotation: props.onClickAnnotation, className: props.optionClassName, selectType: props.selectType, renderCheckbox: props.renderCheckbox }, opt.id));
    });
    if (props.noneOption != null) {
        const target = props.noneOption.media ? mediaOptions : textOptions;
        const isSelected = Array.isArray(props.value) && props.value.length === 0;
        target.push(jsx(SelectOption, { option: props.noneOption, autoFocus: !props.options.length && props.autoFocus, selected: isSelected, handleClick: () => props.onChange(isSelected ? undefined : [], true), fileUrlProvider: props.fileUrlProvider, onClickAnnotation: props.onClickAnnotation, className: props.optionClassName, selectType: props.selectType, renderCheckbox: props.renderCheckbox }, "opt-none"));
    }
    const mediaContainer = mediaOptions.length ? (jsx("div", { className: "select-container media-content " + (props.className || ""), children: mediaOptions })) : null;
    const textContainer = textOptions.length ? (jsx("div", { className: "select-container text-content " + (props.className || ""), children: textOptions })) : null;
    if (mediaContainer && !textContainer) {
        return mediaContainer;
    }
    else if (textContainer && !mediaContainer) {
        return textContainer;
    }
    else {
        return (jsxs("div", { className: "select-container " + (props.className || ""), children: [mediaContainer, textContainer] }));
    }
}
function SelectOption(props) {
    const opt = props.option;
    const ref = useRef(undefined);
    const isMultipleChoice = props.selectType === SelectComponentType.MULTIPLE_CHOICE;
    useAutoFocus(ref.current, props.autoFocus);
    const infoButton = opt.annotation ? (jsx("div", { onClick: e => {
            e.preventDefault();
            e.stopPropagation();
            props.onClickAnnotation(e, opt.annotation, opt);
        }, className: "radio-button info-button", children: jsx(ReaderIcon, { iconName: "help" }) })) : null;
    const mediaDisplay = opt.media ? (jsx(MediaDisplay, { media: opt.media.file, caption: opt.media.caption, fileUrlProvider: props.fileUrlProvider, className: "option-media" })) : null;
    const isSelected = props.selected;
    const iconChecked = props.selectType == SelectComponentType.MULTIPLE_CHOICE ? "check_box" : "check_circle";
    const iconUnchecked = props.selectType == SelectComponentType.MULTIPLE_CHOICE ? "check_box_outline_blank" : "radio_button_unchecked";
    let checkbox = null;
    if (props.showCheckbox) {
        checkbox = props.renderCheckbox
            ? props.renderCheckbox(isSelected)
            : (jsx(ReaderIcon, { className: "checkbox-icon", iconName: isSelected ? iconChecked : iconUnchecked }));
    }
    const labelAsPlainText = richToPlain(opt.label);
    return (jsxs("div", Object.assign({}, onActivate(() => props.handleClick(opt)), { ref: ref, title: labelAsPlainText, "aria-label": labelAsPlainText, role: isMultipleChoice ? "checkbox" : "button", "aria-checked": isMultipleChoice ? isSelected : undefined, "aria-pressed": !isMultipleChoice ? isSelected : undefined, className: "option " + (isSelected ? "selected " : "") + (props.className || ""), children: [mediaDisplay, jsxs("div", { className: "option-detail", children: [checkbox, jsx("div", { className: "option-label", "aria-hidden": true, children: jsx(RichText, { text: opt.label }) }), infoButton] })] })));
}
function shouldSubmitOnSelect(nodeInput, node) {
    var _a;
    const onlyInputField = isInputNode(node) && node.inputFields.length == 1 ? node.inputFields[0] : undefined;
    return nodeInput != undefined
        && onlyInputField
        && ((_a = onlyInputField["displayType"]) !== null && _a !== void 0 ? _a : "RADIO_BUTTONS") === "RADIO_BUTTONS"
        && (isBooleanInputField(onlyInputField) || isSingleChoiceInputField(onlyInputField))
        && !isEmptyNodeInput(nodeInput);
}

function BooleanInput(props) {
    var _a;
    const inputField = props.inputField;
    const val = props.value;
    const displayType = (_a = inputField.displayType) !== null && _a !== void 0 ? _a : BooleanDisplayType.RADIO_BUTTONS;
    const trueOption = { id: 0, idx: 0, label: inputField.trueLabel || "✓" };
    const falseOption = { id: 1, idx: 1, label: inputField.falseLabel || "x" };
    switch (displayType) {
        case "RADIO_BUTTONS":
            return (jsx(SelectComponent, { options: [trueOption, falseOption], value: ((val === null || val === void 0 ? void 0 : val.value) != null) ? (val.value ? trueOption.id : falseOption.id) : undefined, onChange: (id, isConfirmed) => props.onChange(id != null
                    ? BooleanInputUtils.asNodeInput(id == trueOption.id)
                    : createEmptyNodeInput(), isConfirmed), selectType: SelectComponentType.SINGLE_CHOICE, isOptional: !inputField.required, onClickAnnotation: (e, txt) => props.menu.showMenu({
                    anchor: e.currentTarget,
                    render: () => (jsx(InfoBox, { text: txt, config: props.config, onClose: props.menu.dismissMenu }))
                }), className: props.selectComponentClassName, optionClassName: props.selectComponentOptionClassName, fileUrlProvider: props.fileUrlProvider, autoFocus: props.autoFocus }));
        case "CHECKBOX":
            return (jsx(SelectComponent, { options: [trueOption], value: (val === null || val === void 0 ? void 0 : val.value) ? [trueOption.idx] : [], onChange: (idcs, isConfirmed) => props.onChange(BooleanInputUtils.asNodeInput(!!idcs.length), isConfirmed), showCheckboxes: true, selectType: SelectComponentType.MULTIPLE_CHOICE, isOptional: !inputField.required, onClickAnnotation: (e, txt) => props.menu.showMenu({
                    anchor: e.currentTarget,
                    render: () => (jsx(InfoBox, { text: txt, config: props.config, onClose: props.menu.dismissMenu }))
                }), className: props.selectComponentClassName, optionClassName: props.selectComponentOptionClassName, fileUrlProvider: props.fileUrlProvider, autoFocus: props.autoFocus }));
    }
}

function DynamicChoiceInputComponent(props) {
    const inputField = props.inputField;
    const val = props.value;
    const key = val === null || val === void 0 ? void 0 : val.key;
    const api = createDynamicChoiceApiRoutesFromUrl(inputField.apiUrl);
    return (jsx(AutoComplete, { autoFocus: props.autoFocus, value: key, onChange: (key, opt) => props.onChange(DynamicChoiceInputUtils.asNodeInput({ key: key, label: opt.label })), onClear: () => props.onChange(createEmptyNodeInput()), config: props.config, autoCompleteConfig: {
            listOptions: (query) => {
                const promise = (query === null || query === void 0 ? void 0 : query.length) > 0
                    ? api.getOptionsForQuery(query)
                    : api.getRecommendedOptions();
                return promise.then(opts => opts);
            },
            getOptionForKey: (key) => api.getOptionForKey(key).then(opt => opt),
            enableKeyboardControls: true
        }, className: props.autoCompleteComponentClassName, fileUrlProvider: props.fileUrlProvider, onClickAnnotation: (e, annotation) => props.menu.showMenu({
            anchor: e.currentTarget,
            render: () => (jsx(InfoBox, { text: annotation, onClose: props.menu.dismissMenu, config: props.config }))
        }), renderInput: props.renderInput }));
}

function FileInput(props) {
    var _a, _b, _c, _d, _e, _f;
    const file = (_a = props.value) === null || _a === void 0 ? void 0 : _a.file;
    const [fileInfo, setFileInfo] = useState(file);
    const [toUpload, setToUpload] = useState(null);
    const fileInputRef = useRef();
    const ref = useRef();
    useAutoFocus(ref.current, props.autoFocus);
    function handlePickFile(file) {
        setFileInfo(null);
        setToUpload(file);
        props.storeFile(file);
        props.onChange(FileInputUtils.asNodeInput(file));
    }
    function handleReset() {
        setFileInfo(null);
        setToUpload(null);
        props.storeFile(null);
        props.onChange(createEmptyNodeInput());
    }
    const hasFile = ((fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.id) != null) || (toUpload != null);
    const fileName = (fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.name) || (toUpload === null || toUpload === void 0 ? void 0 : toUpload.name);
    const fileSizeBytes = (_c = (_b = fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.size) !== null && _b !== void 0 ? _b : toUpload === null || toUpload === void 0 ? void 0 : toUpload.size) !== null && _c !== void 0 ? _c : 0;
    const fileSize = FileInputUtils.formatFileSize(fileSizeBytes);
    function renderFile(props) {
        return (jsxs("div", { className: "file-upload file", children: [jsx(ReaderIcon, { className: "icon", iconName: "description" }), jsx("div", { className: "text", children: props.fileName }), jsx("div", { className: `note${props.note.isError ? " error" : ""}`, children: props.note.text }), jsx(ReaderIcon, Object.assign({ iconName: "clear", className: "remove-icon" }, onActivate(props.onClear)))] }));
    }
    let fileView = null;
    if (hasFile) {
        /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
        const maxFileSize = window.__cdc_maxFileUploadSize;
        const maxFileSizeText = maxFileSize != null ? FileInputUtils.formatFileSize(maxFileSize) : null;
        const fileSizeExceeded = fileSizeBytes > maxFileSize;
        const fileSizeText = fileSizeExceeded
            ? `${fileSize} / ${maxFileSizeText}`
            : fileSize;
        const invalidExtension = props.allowedExtensions
            && toUpload != null
            && !props.allowedExtensions.includes(FileInputUtils.getLowerCaseExtensionFromFileName(toUpload.name));
        const noteText = invalidExtension
            ? `≠ ${((_d = props.allowedExtensions) === null || _d === void 0 ? void 0 : _d.map(it => it.toUpperCase()).join(", ")) || " N/A"}`
            : fileSizeText;
        const noteIsError = fileSizeExceeded || invalidExtension;
        const renderFileProps = {
            fileName: fileName || "",
            note: {
                isError: noteIsError,
                text: noteText
            },
            onClear: handleReset,
        };
        fileView = props.renderFile ? props.renderFile(renderFileProps) : renderFile(renderFileProps);
    }
    const allowedExtensions = (_f = (_e = props.allowedExtensions) === null || _e === void 0 ? void 0 : _e.map(ext => "." + ext)) === null || _f === void 0 ? void 0 : _f.join(",");
    const uploadButton = !hasFile ? (jsxs("div", Object.assign({ className: "file-upload upload-button" }, onActivate(() => { var _a; return (_a = fileInputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }), { ref: ref, children: [jsx(ReaderIcon, { iconName: "cloud_upload", className: "icon" }), jsx("div", { className: "text", children: props.config.messages.inputFileUpload }), jsx("input", { ref: fileInputRef, type: "file", style: { display: "none" }, tabIndex: -1, accept: allowedExtensions, onChange: e => handlePickFile(e.target.files[0]) })] }))) : null;
    return uploadButton || fileView;
}

function getNumberInputConstraintState(field, inputText, config) {
    const { minValue, maxValue } = NumberInputUtils.getNumberConstraints(field);
    if (minValue != null || maxValue != null) {
        const validation = NumberInputUtils.validateNumber(field, inputText);
        let infoText = "";
        if (validation.lowerLimitExceeded) {
            infoText = (config.messages.inputMinNumber || "Min. $0").replace("$0", String(minValue));
        }
        else if (validation.upperLimitExceeded) {
            infoText = (config.messages.inputMaxNumber || "Max. $0").replace("$0", String(maxValue));
        }
        else if (inputText.length) {
            infoText = inputText + ((maxValue != null) ? (" / " + maxValue) : "");
        }
        return {
            hint: infoText,
            valid: validation.valid,
        };
    }
    return null;
}
function renderNumberInputConstraints(field, inputText, config) {
    const constraintState = getNumberInputConstraintState(field, inputText, config);
    if (constraintState) {
        return (jsx("div", { className: "text-input-constraints " + (constraintState.valid ? "" : " error"), children: constraintState.hint }));
    }
    else {
        return null;
    }
}

function SingleChoiceInput(props) {
    var _a;
    const inputField = props.inputField;
    const val = props.value;
    const optionId = val === null || val === void 0 ? void 0 : val.optionId;
    const displayType = (_a = inputField.displayType) !== null && _a !== void 0 ? _a : SingleChoiceDisplayType.RADIO_BUTTONS;
    const options = inputField.options;
    const handleAnnotationClick = (e, annotation) => props.menu.showMenu({
        anchor: e.currentTarget,
        render: () => (jsx(InfoBox, { text: annotation, onClose: props.menu.dismissMenu, config: props.config }))
    });
    function handleSelectId(optionId, isConfirmed) {
        props.onChange((optionId !== null && optionId !== void 0 ? optionId : null) === null ? createEmptyNodeInput() : SingleChoiceInputUtils.asNodeInput(optionId), isConfirmed);
    }
    switch (displayType) {
        case "RADIO_BUTTONS":
            return (jsx(SelectComponent, { options: options, value: optionId, onChange: (id, isConfirmed) => handleSelectId((id !== null && id !== void 0 ? id : null) !== null ? id : undefined, isConfirmed), selectType: SelectComponentType.SINGLE_CHOICE, isOptional: !inputField.required, onClickAnnotation: handleAnnotationClick, className: props.selectComponentClassName, optionClassName: props.selectComponentOptionClassName, fileUrlProvider: props.fileUrlProvider, autoFocus: props.autoFocus }));
        case "AUTOCOMPLETE":
            return (jsx(AutoComplete, { autoFocus: props.autoFocus, value: optionId, onChange: optionId => handleSelectId(optionId, true), onClear: !inputField.required ? () => handleSelectId(undefined, true) : undefined, config: props.config, autoCompleteConfig: {
                    listOptions: (query) => new Promise(resolve => resolve(options
                        .filter(opt => {
                        var _a;
                        return (query.length && ((_a = richToPlain(opt.label)) === null || _a === void 0 ? void 0 : _a.toLowerCase().includes(query.toLowerCase())));
                    })
                        .map(inputOptionAsAutoCompleteOption))),
                    getOptionForKey: (key) => new Promise(resolve => resolve(inputOptionAsAutoCompleteOption(options.find(opt => opt.id == key)))),
                    enableKeyboardControls: true
                }, className: props.autoCompleteComponentClassName, fileUrlProvider: props.fileUrlProvider, onClickAnnotation: handleAnnotationClick }));
        case "DROPDOWN":
            return (jsx(Dropdown, { autoFocus: props.autoFocus, className: props.dropdownComponentClassName, options: options.map(opt => (Object.assign(Object.assign({}, opt), { media: opt.media }))).sort((a, b) => a.idx - b.idx), menuControls: props.menu, fileUrlProvider: props.fileUrlProvider, placeholder: props.config.messages.inputSelect, value: optionId, onChange: (id) => handleSelectId(id, true), onClear: (!inputField.required || optionId) ? e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectId(undefined, true);
                } : undefined }));
    }
}
function inputOptionAsAutoCompleteOption(opt) {
    var _a;
    return opt && { key: opt.id, label: opt.label, media: (_a = opt.media) === null || _a === void 0 ? void 0 : _a.file, annotation: opt.annotation };
}

// getLanguage gets the current application language in short string format (like "en", "de", ...)
function getDefaultLanguageFromBrowser() {
    const lang = document.cookie.match(/lang=(.*?)([;\s]|$)/g);
    if (lang != null && lang[1]) {
        return lang[1];
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return navigator.language || navigator.userLanguage || "en";
}
function createMessages(lang) {
    const defaultLang = "en";
    const browserLang = getDefaultLanguageFromBrowser();
    const targetLang = lang || browserLang;
    function msgOf(translations) {
        return translations[targetLang] || translations[defaultLang] || translations[defaultLang];
    }
    return {
        meta: {
            langCode: targetLang,
        },
        state: {
            loading: msgOf({
                en: "Just one moment...",
                de: "Einen Moment...",
                fr: "Un instant...",
            }),
            waiting: msgOf({
                en: "Continuing...",
                de: "Wird bearbeitet...",
                fr: "En train d'exécution...",
            }),
            waitingLong: msgOf({
                en: "This may take a while...",
                de: "Bitte haben Sie etwas Geduld...",
                fr: "Cela peut prendre un moment...",
            })
        },
        input: {
            skip: msgOf({
                en: "Skip",
                de: "Überspringen",
                fr: "Passer",
            }),
            continue: msgOf({
                en: "Continue",
                de: "Weiter",
                fr: "Continuer",
            }),
            openNested: msgOf({
                en: "Open",
                de: "Öffnen",
                fr: "Ouvrir",
            }),
            boolean: {
                true: msgOf({
                    en: "Yes",
                    de: "Ja",
                    fr: "Oui",
                }),
                false: msgOf({
                    en: "No",
                    de: "Nein",
                    fr: "Non",
                }),
            },
            file: {
                file: msgOf({
                    en: "File",
                    de: "Datei",
                    fr: "Fichier",
                })
            },
            multipleChoice: {
                none: msgOf({
                    en: "None",
                    de: "Keine",
                    fr: "Aucun",
                })
            },
            chat: {
                input: msgOf({
                    en: "Send message",
                    de: "Nachricht senden",
                    fr: "Envoyer un message",
                })
            }
        },
        validation: {
            emailInvalid: msgOf({
                en: "Email address is invalid",
                de: "E-Mail-Adresse ist ungültig",
                fr: "L'adresse e-mail est invalide",
            }),
            ibanInvalid: msgOf({
                en: "IBAN is invalid",
                de: "IBAN ist ungültig",
                fr: "L'IBAN est invalide",
            }),
            phoneNumberInvalid: msgOf({
                en: "Phone number is invalid",
                de: "Telefonnummer ist ungültig",
                fr: "Le numéro de téléphone est invalide",
            }),
            jsonInvalid: msgOf({
                en: "JSON is invalid",
                de: "JSON ist ungültig",
                fr: "Le JSON est invalide",
            }),
        }
    };
}
const MessagesContext = React.createContext(null);
function useMessages() {
    return React.useContext(MessagesContext);
}

function useTextInputConstraintValidation(field, config) {
    const messages = useMessages();
    const { minLength, maxLength, pattern } = TextInputUtils.getTextConstraints(field);
    return inputText => {
        if (minLength != null || maxLength != null || pattern != null || field.stringValueType) {
            const length = inputText.length;
            const validation = TextInputUtils.validateInput(field, inputText);
            let infoText = "";
            if (validation.lowerLimitExceeded) {
                infoText = config.messages.inputMinTextLength.replace("$0", String(minLength));
            }
            else if (validation.patternViolated && inputText.length) {
                infoText = config.messages.inputTextInvalid;
            }
            else if (validation.valueConstraintViolated && inputText.length) {
                if (field.stringValueType === "EMAIL") {
                    infoText = messages.validation.emailInvalid;
                }
                else if (field.stringValueType === "IBAN") {
                    infoText = messages.validation.ibanInvalid;
                }
                else if (field.stringValueType === "PHONE_NUMBER") {
                    infoText = messages.validation.phoneNumberInvalid;
                }
                else if (field.stringValueType === "JSON") {
                    infoText = messages.validation.jsonInvalid;
                }
            }
            else if (inputText.length) {
                infoText = length + ((maxLength != null) ? (" / " + maxLength) : "");
            }
            return {
                hint: infoText,
                valid: validation.valid,
            };
        }
        return null;
    };
}
function TextInputConstraints(props) {
    const constraintValidation = useTextInputConstraintValidation(props.field, props.config);
    const validation = constraintValidation(TextInputUtils.cleanInput(props.inputText, props.field.stringValueType));
    if (validation) {
        return (jsx("div", { className: "text-input-constraints " + (validation.valid ? "" : " error"), children: validation.hint }));
    }
    else {
        return null;
    }
}

const ReaderStatusEvents = {
    RUN_STARTED: "RUN_STARTED",
    RUN_FINISHED: "RUN_FINISHED",
    RUN_STEP: "RUN_STEP",
    DISMISS_PORTAL: "DISMISS_PORTAL"
};
/**
 *
 * @param event
 * @param targetPublic Whether to send this message to all origins, instead of only same origin, i.e. another reader instance
 */
function postEventMessage(event, targetPublic) {
    if (window && window.parent && window.parent.postMessage) {
        const isParentSameOrigin = document.referrer.startsWith(window.origin);
        if (targetPublic || isParentSameOrigin) {
            window.parent.postMessage(event, targetPublic ? "*" : window.origin);
        }
    }
    else if (console && console.warn) {
        console.warn("Could not send event with window#postMessage, window.parent.postMessage was not defined");
    }
}
const ReaderStatus = {
    RUN_STARTED: (run) => addEventParams(ReaderStatusEvents.RUN_STARTED, run),
    RUN_STEP: (run) => addEventParams(ReaderStatusEvents.RUN_STEP, run),
    RUN_FINISHED: (run) => addEventParams(ReaderStatusEvents.RUN_FINISHED, run),
    DISMISS_PORTAL: (run) => addEventParams(ReaderStatusEvents.DISMISS_PORTAL, run)
};
function addEventParams(eventName, data) {
    return Object.assign(Object.assign({}, data), { event: eventName, eventSrc: CDC_EVENT_SOURCE, modelId: data.state.modelId, runId: data.state.runId });
}
const CDC_EVENT_SOURCE = "cdc-event";

function useNestedModelPortal() {
    const [nestedRunUrl, setNestedRunUrl] = useState(undefined);
    const [finishedRunUrl, setFinishedRunUrl] = useState(undefined);
    // Capture DISMISS_PORTAL event
    useEffect(() => {
        const eventListener = (e) => {
            if (e.origin == window.origin
                && e.data.eventSrc == CDC_EVENT_SOURCE
                && e.data.event == ReaderStatusEvents.DISMISS_PORTAL
                && (nestedRunUrl === null || nestedRunUrl === void 0 ? void 0 : nestedRunUrl.includes(e.data.runId))) { // TODO rather equals, but run id is not available here
                setFinishedRunUrl(nestedRunUrl);
            }
        };
        window.addEventListener("message", eventListener);
        return () => window.removeEventListener("message", eventListener);
    });
    const isFinished = finishedRunUrl == nestedRunUrl;
    // Use keyboard shortcut
    useEffect(() => {
        const dismissShortcutListener = (e) => {
            if (isFinished && (e.ctrlKey || e.shiftKey) && e.key === "Enter") {
                setNestedRunUrl(undefined);
            }
        };
        window.addEventListener("keydown", dismissShortcutListener);
        return () => window.removeEventListener("keydown", dismissShortcutListener);
    }, [isFinished]);
    const isShown = !!nestedRunUrl && !isFinished;
    const isNestedPortal = isContainedInNestedModelPortal();
    const iframeUrl = nestedRunUrl
        ? (nestedRunUrl + (nestedRunUrl.includes("?") ? "&" : "?") + "portal=true")
        : nestedRunUrl;
    const portal = (jsx("div", { className: "cdc-nested-model-portal" + (isShown ? " shown " : "") + (isNestedPortal ? " nested-portal" : ""), children: jsx("div", { className: "portal-container", children: jsx("iframe", { src: iframeUrl }) }) }, "nm-portal"));
    const controls = useMemo(() => ({ showNestedRun: setNestedRunUrl }), [setNestedRunUrl]);
    return [controls, portal, isShown];
}
function isContainedInNestedModelPortal() {
    return window.location.search.includes("portal=true");
}

function applyFontsFromConfig(config) {
    const fontDefinitions = [
        { key: "headingFont", font: config.headingFont },
        { key: "baseFont", font: config.font },
    ];
    fontDefinitions
        .filter(f => f.font != undefined)
        .forEach(fontDef => {
        var _a, _b, _c;
        // Apply font
        const fontFamily = (_a = fontDef.font) === null || _a === void 0 ? void 0 : _a.fontFamily;
        const fontLinkUrl = (_b = fontDef.font) === null || _b === void 0 ? void 0 : _b.linkUrl;
        const fontSrcUrl = (_c = fontDef.font) === null || _c === void 0 ? void 0 : _c.srcUrl;
        if (fontFamily) {
            const targetClass = fontDef.key === "headingFont" ? ".node-description" : "body";
            let styleAsText = targetClass + " *:not(.material-icons) { font-family: \"" + fontFamily + "\", sans-serif !important; }";
            if (fontSrcUrl) {
                styleAsText = "@font-face { font-family: '" + fontFamily + "'; src: url('" + fontSrcUrl + "'); } " + styleAsText;
            }
            else if (fontLinkUrl) {
                const linkElement = document.createElement("link");
                linkElement.href = fontLinkUrl;
                linkElement.rel = "stylesheet";
                document.head.appendChild(linkElement);
            }
            const styleElement = document.createElement("style");
            styleElement.type = "text/css";
            styleElement.appendChild(document.createTextNode(styleAsText));
            document.head.appendChild(styleElement);
        }
        else {
            console.warn("Custom font defined in reader config, but font-family attribute was not set: Font was not applied.");
        }
    });
}

/**
 * ReaderApp is a CODIAC Reader specific extension of ReaderModule,
 * including ReaderConfigs and run status events
 */
class ReaderApp extends ReaderModule {
    constructor(conf) {
        super(conf);
        Object.defineProperty(this, "_readerAppConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "_readerConfig", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this._readerAppConfig = conf;
        this._readerConfig = conf.readerConfig;
        this._applyReaderConfig();
    }
    getReaderConfig() {
        return this._readerConfig;
    }
    _applyReaderConfig() {
        // Apply fonts
        applyFontsFromConfig(this._readerConfig);
        // Move custom CSS tag to bottom of head element to give it priority
        const customCSSTag = document.getElementById("cdc-custom-css");
        if (customCSSTag) {
            document.head.removeChild(customCSSTag);
            document.head.appendChild(customCSSTag);
        }
    }
    _handleRunUpdated(run) {
        var _a, _b, _c, _d, _e;
        // Emit status events
        if (!!run.result && !((_a = this.getRun()) === null || _a === void 0 ? void 0 : _a.result)) {
            postEventMessage(ReaderStatus.RUN_FINISHED(run), true);
        }
        else if (this.didRunChange(this._run, run)) {
            // Send run started event if we're at the first node
            if (((_d = (_c = (_b = run === null || run === void 0 ? void 0 : run.state) === null || _b === void 0 ? void 0 : _b.history) === null || _c === void 0 ? void 0 : _c.path) === null || _d === void 0 ? void 0 : _d.length) === 1) {
                postEventMessage(ReaderStatus.RUN_STARTED(run), true);
            }
            // Check for run changed status event before calling super, as the last run state (this._run) will be updated
            postEventMessage(ReaderStatus.RUN_STEP(run), !this._readerConfig.disableStatusEvents);
        }
        // Call super
        super._handleRunUpdated(run);
        // Call callback
        (_e = this._readerAppConfig.eventHandlers) === null || _e === void 0 ? void 0 : _e.onRunUpdated(run);
        return run;
    }
}

Sentry.init({
    dsn: "https://6c60431632bbc2d5a4f72cf7c9b4d4c9@o4508342190669824.ingest.de.sentry.io/4508342195191888",
    integrations: [],
    environment: getEnvironmentName()
});
function getEnvironmentName() {
    var _a;
    const host = (_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.host;
    if (host) {
        if (host.includes("staging.")) {
            return "staging";
        }
        else {
            return ["codiac.de", "codiac.com"].some(it => host.endsWith(it)) ? "production" : "development";
        }
    }
    else {
        return "production";
    }
}
function appWithErrorHandlerOf(Component) {
    return Sentry.withErrorBoundary(Component, {
        fallback: () => {
            const lang = window.navigator.language || "en";
            const text = {
                "de": "Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.",
            }[lang] || "An error occurred. Please try again later.";
            return (jsx("p", { children: text }));
        }
    });
}

export { ActionButton, AutoComplete, BooleanInput, CDC_EVENT_SOURCE, DynamicChoiceInputComponent, ErrorState, FileInput, HTMLDisplay, HighlightedMedia, InfoBox, MessagesContext, ProgressBar, ProgressIndicator, ReaderApp, ReaderIcon, ReaderStatus, ReaderStatusEvents, Scrollbar, SelectComponent, SelectComponentType, SelectOption, SingleChoiceInput, TextInputConstraints, appWithErrorHandlerOf, applyFontsFromConfig, createMessages, getCSSVariable, getNumberInputConstraintState, isContainedInNestedModelPortal, postEventMessage, renderNumberInputConstraints, shouldSubmitOnSelect, useAccessibilityOptions, useMessages, useNestedModelPortal, useTextInputConstraintValidation };
